import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
import { numberWithCommas } from 'site-modules/shared/utils/string';
import { isNew } from 'site-modules/shared/utils/inventory-utils/is-new';
import { isCpo } from 'site-modules/shared/utils/inventory-utils/is-cpo';
import { getOfferText } from 'site-modules/shared/utils/inventory-utils/get-offer-text';
import { formatPriceString } from 'site-modules/shared/utils/price-utils';
import { InventoryEntities } from 'client/data/models/inventory';
import { showTrimName } from 'client/site-modules/shared/utils/show-trim-name';
import { DEFAULT_PRICE } from 'client/site-modules/shared/components/deal/deal-constants';
import { isPartnerDealer } from 'site-modules/shared/utils/dealer-details-utils';

/**
 * To determine the price label.
 * @param {Boolean} isNewVin - Determines whether the vin is a new vin type.
 * @param {Boolean} showDealerPrice - Determines whether it's a tranparent pricing type.
 */
export function getPriceLabel(isNewVin, showDealerPrice) {
  if (showDealerPrice || !isNewVin) {
    return 'Price';
  }
  return 'List';
}

/**
 * Inventory card description.
 * @param {Object} vinData
 * @param {Boolean} showDealerName
 * @param {Boolean} labelId
 * @param {Boolean} describedId
 * @returns {ReactElement}
 */
export function SimilarInventoryCardDescription({
  vinData,
  showDealerName = false,
  labelId = null,
  describedId = null,
}) {
  const year = get(vinData, 'vehicleInfo.styleInfo.year', '');
  const make = get(vinData, 'vehicleInfo.styleInfo.make', '');
  const model = get(vinData, 'vehicleInfo.styleInfo.model', '');
  const displayPrice = get(vinData, 'prices.displayPrice');
  const type = get(vinData, 'type', '');
  const trim = get(vinData, 'vehicleInfo.styleInfo.trim', '');
  const dealerDistance = get(vinData, 'dealerInfo.distance');
  const dealerName = get(vinData, 'dealerInfo.name', 'Not Available');
  const mileage = get(vinData, 'vehicleInfo.mileage');
  const isNewVin = isNew(type);
  const offerText = getOfferText(vinData);
  const mmy = `${year} ${make} ${model}`;
  const guaranteedPrice = get(vinData, 'prices.guaranteedPrice');
  const isPartner = isPartnerDealer(vinData);
  const hasGuaranteedPrice = !!(guaranteedPrice && guaranteedPrice > 0);
  const showDealerPrice = isNewVin && isPartner && hasGuaranteedPrice && !['Honda', 'Acura'].includes(make);
  const transparentPrice = showDealerPrice ? guaranteedPrice : displayPrice;
  const priceLabel = getPriceLabel(isNewVin, showDealerPrice);

  return (
    <div className="inventory-card-description-block p-0_25 p-md-0_5 d-flex flex-column justify-content-between">
      <div className="d-flex flex-column justify-content-between" id={labelId}>
        <div className="text-uppercase disclaimer text-gray-dark mb-1">{isCpo(type) ? 'Certified' : type}</div>
        <div title={`${mmy} ${showTrimName(make, trim) ? trim : ''}`.trim()}>
          <div className="h6 text-gray-darker fw-medium text-truncate mb-0">{mmy}</div>
          <div className="h6 text-gray-darker fw-medium text-truncate">{showTrimName(make, trim) && trim}</div>
        </div>
      </div>
      <div className="d-flex flex-column justify-content-between" id={describedId}>
        {!!mileage && !isNewVin && (
          <div className="miles-number text-gray-dark disclaimer mb-1">{numberWithCommas(mileage)} miles</div>
        )}
        <div className="inventory-price-section">
          <span>
            <span className="disclaimer text-gray-dark">{priceLabel}:&nbsp;</span>
            <span className="m-0 medium fw-medium">
              {transparentPrice ? formatPriceString(transparentPrice) : DEFAULT_PRICE}
            </span>
          </span>
          <div className="inventory-price-promise-text fw-normal text-primary pb-md-0_25 m-0 h6">{offerText}</div>
        </div>
        {showDealerName && (
          <div className="inventory-dealer-name">
            <span className="d-block heading-6">{dealerName}</span>
            {!!dealerDistance && <span className="d-block xsmall text-gray">{dealerDistance.toFixed(1)} mi away</span>}
          </div>
        )}
      </div>
    </div>
  );
}
SimilarInventoryCardDescription.propTypes = {
  vinData: InventoryEntities.InventoryVin.isRequired,
  showDealerName: PropTypes.bool,
  labelId: PropTypes.string,
  describedId: PropTypes.string,
};
